// Picker sheets shared by several screens: plan a day+slot, choose a mealplan day,
// choose a cookbook, choose a recipe.
import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Sheet from './Sheet';
import Photo from './Photo';
import { Caption, Chip, Field, ListItem, Row } from './ui';
import { addDays } from '../logic/dates';
import { globalScore, GLOBAL_MIN_SCORE } from '../logic/search';
import { useFormatDay, useDiet } from '../hooks';
import { useLibrary } from '../store/library';
import { CONTENT_RECIPES } from '../../content';
import { spacing } from '../theme';
import type { Day, Mealplan, Recipe, Slot } from '../types';
import type { IconName } from './Icon';

export const SLOTS: { slot: Slot; icon: IconName }[] = [
  { slot: 'breakfast', icon: 'breakfast' },
  { slot: 'lunch', icon: 'lunch' },
  { slot: 'dinner', icon: 'dinner' },
];

/** Pick a day (next 21 days) and a meal slot. */
export function PlanPickerSheet({ visible, onClose, today, onPick }: { visible: boolean; onClose: () => void; today: Day; onPick: (day: Day, slot: Slot) => void }) {
  const formatDay = useFormatDay();
  const { t } = useTranslation();
  const [day, setDay] = useState(today);
  const days = useMemo(() => Array.from({ length: 21 }, (_, i) => addDays(today, i)), [today]);
  return (
    <Sheet visible={visible} onClose={onClose} title={t('library.addToPlanner')}>
      <Row>
        {days.map(d => (
          <Chip key={d} label={d === today ? t('common.today') : formatDay(d)} selected={d === day} onPress={() => setDay(d)} />
        ))}
      </Row>
      {SLOTS.map(s => (
        <ListItem key={s.slot} title={t(`planner.${s.slot}`)} icon={s.icon} onPress={() => onPick(day, s.slot)} />
      ))}
    </Sheet>
  );
}

/** Pick a mealplan, then a cycle day and slot. */
export function MealplanPickerSheet({ visible, onClose, onPick }: { visible: boolean; onClose: () => void; onPick: (plan: Mealplan, day: number, slot: Slot) => void }) {
  const { t } = useTranslation();
  const mealplans = useLibrary(s => s.mealplans);
  const [plan, setPlan] = useState<Mealplan | null>(null);
  const [day, setDay] = useState(0);
  const sorted = Object.values(mealplans).sort((a, b) => a.title.localeCompare(b.title));
  return (
    <Sheet visible={visible} onClose={() => { setPlan(null); onClose(); }} title={plan ? plan.title : t('library.addToMealplan')}>
      {!plan ? (
        sorted.length ? sorted.map(m => <ListItem key={m.id} title={m.title} icon="mealplan" onPress={() => setPlan(m)} />) : <Caption>{t('common.empty')}</Caption>
      ) : (
        <>
          <Row>
            {Array.from({ length: plan.cycleDays }, (_, i) => (
              <Chip key={i} label={t('mealplan.day', { n: i + 1 })} selected={i === day} onPress={() => setDay(i)} />
            ))}
          </Row>
          {SLOTS.map(s => (
            <ListItem key={s.slot} title={t(`planner.${s.slot}`)} icon={s.icon} onPress={() => { onPick(plan, day, s.slot); setPlan(null); }} />
          ))}
        </>
      )}
    </Sheet>
  );
}

export function CookbookPickerSheet({ visible, onClose, onPick }: { visible: boolean; onClose: () => void; onPick: (cookbookId: string) => void }) {
  const { t } = useTranslation();
  const cookbooks = useLibrary(s => s.cookbooks);
  const sorted = Object.values(cookbooks).sort((a, b) => a.title.localeCompare(b.title));
  return (
    <Sheet visible={visible} onClose={onClose} title={t('library.addToCookbook')}>
      {sorted.length ? sorted.map(c => <ListItem key={c.id} title={c.title} icon="cookbook" onPress={() => onPick(c.id)} />) : <Caption>{t('common.empty')}</Caption>}
    </Sheet>
  );
}

/** Search your recipes and the country cookbooks. */
export function RecipePickerSheet({ visible, onClose, onPick, title }: { visible: boolean; onClose: () => void; onPick: (recipe: Recipe) => void; title?: string }) {
  const { t } = useTranslation();
  const own = useLibrary(s => s.recipes);
  const [query, setQuery] = useState('');
  const diet = useDiet();
  const all = useMemo(() => [...Object.values(own), ...Object.values(CONTENT_RECIPES)].filter(r => !diet.hidden(r)), [own, diet]);
  const shown = useMemo(() => {
    if (query.trim().length < 2) return all;
    return all
      .map(r => ({ r, s: globalScore(query, r.title, r.explainer, []) }))
      .filter(x => x.s >= GLOBAL_MIN_SCORE)
      .sort((a, b) => b.s - a.s)
      .map(x => x.r);
  }, [all, query]);
  return (
    <Sheet visible={visible} onClose={onClose} title={title ?? t('planner.chooseRecipe')} full>
      <Field placeholder={t('common.search')} value={query} onChangeText={setQuery} autoCorrect={false} />
      <View style={{ gap: spacing.s }}>
        {shown.map(r => (
          <ListItem key={r.id} title={r.title} diets={diet.issues(r)} subtitle={t('common.minutes', { count: r.durationMin })} leading={<Photo media={r.image} seed={r.id} style={{ width: 44, height: 44 }} iconSize={20} />} onPress={() => onPick(r)} chevron={false} />
        ))}
      </View>
    </Sheet>
  );
}
